import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, MapPin, Award, UserCheck, ArrowRight, Camera, Bell, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSettings } from "@/lib/settings";

const SPECIALTIES = [
  "CCTV & Security Camera Installation",
  "Ajax Smart Alarm Systems",
  "Alarm Monitoring",
  "Video & Audio Intercoms",
  "Commercial Security Solutions",
  "Residential Security Systems",
  "Night Vision Camera Systems",
  "Ongoing Maintenance & Support",
];

const DEFAULT_BIO = "Mohammad founded Eagle Eye Alarm Monitoring & Security Services after building his career in the security industry, and has spent the years since earning a reputation across Brisbane's southside for straightforward advice and careful, tidy installation work. He personally handles every job from the first walk-through to the final cable tie — no call centre, no subcontractors passing the job along. The person who quotes your install is the same person who installs it, and the same person you'll reach afterwards if anything needs attention.";

export default function AboutPage() {
  const { settings } = useSettings();
  const years = settings?.about_years_experience || 15;
  const bio = settings?.about_bio || DEFAULT_BIO;

  return (
    <div>
      {/* Intro */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-16 pb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">About Eagle Eye</div>
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 items-start">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="relative">
            <div className="w-full aspect-square rounded-2xl overflow-hidden border border-border bg-secondary">
              {settings?.about_photo ? (
                <img src={settings.about_photo} alt="Mohammad — Eagle Eye" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="h-20 w-20 rounded-full bg-eagle-gold/15 text-eagle-gold flex items-center justify-center font-display text-3xl font-bold">M</div>
                </div>
              )}
            </div>
            <div className="absolute -bottom-3 -right-3 h-11 w-11 rounded-full bg-eagle-gold text-eagle-ink flex items-center justify-center shadow-lg">
              <ShieldCheck className="h-5 w-5" strokeWidth={2} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-1">Mohammad</h1>
            <div className="text-eagle-gold font-medium mb-5">Owner &amp; Licensed Security Technician</div>
            <p className="text-muted-foreground leading-relaxed mb-6 max-w-2xl">{bio}</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => window.location.assign("/quote")} className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="about-cta-quote">
                Get a Free Quote <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              {settings?.google_review_url && (
                <a href={settings.google_review_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full" data-testid="about-google-reviews">
                    Read reviews on Google <ExternalLink className="h-3.5 w-3.5 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Credentials strip */}
      <section className="border-y border-border bg-secondary/30">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Award, label: "QLD Security License", value: "4272396" },
            { icon: UserCheck, label: "Experience", value: `${years}+ Years` },
            { icon: MapPin, label: "Based in", value: "Brisbane" },
            { icon: ShieldCheck, label: "Every job handled by", value: "Mohammad, personally" },
          ].map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <c.icon className="h-5 w-5 text-eagle-gold mb-2" strokeWidth={1.75} />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{c.label}</div>
              <div className="font-display font-semibold text-sm md:text-base">{c.value}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Specialties */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">What Mohammad specialises in</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8 max-w-xl">One tradesperson, every part of the job.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SPECIALTIES.map((s, i) => (
            <motion.div key={s} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/70 bg-card">
              <CheckCircle2 className="h-4 w-4 text-eagle-gold shrink-0" />
              <span className="text-sm font-medium">{s}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Local + why it matters */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">Local, not a call centre</div>
            <h2 className="font-display text-3xl font-bold tracking-tight mb-4">Servicing Brisbane's southside and beyond.</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Eagle Eye is based in Brisbane and works across greater Brisbane — from the first quote conversation
              through to the install and whatever support comes after. No national call centre, no rotating technicians:
              one licensed person who knows the job because he did it.
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 text-eagle-gold" /> Brisbane · QLD
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-6">
              <Camera className="h-6 w-6 text-eagle-gold mb-3" strokeWidth={1.5} />
              <div className="font-display font-semibold mb-1">Residential</div>
              <p className="text-xs text-muted-foreground">Discreet camera systems for homes across Brisbane.</p>
            </Card>
            <Card className="p-6">
              <Bell className="h-6 w-6 text-eagle-gold mb-3" strokeWidth={1.5} />
              <div className="font-display font-semibold mb-1">Commercial</div>
              <p className="text-xs text-muted-foreground">Multi-site CCTV, alarms &amp; monitored access.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">Talk to Mohammad directly.</h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">Get a free, fixed-price quote with a visual camera plan — no call centre in between.</p>
        <Link to="/quote">
          <Button size="lg" className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover px-8 h-12" data-testid="about-final-cta">
            Get My Free Quote <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
